import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client"
import { useCallback, useState } from "react"
import { COACH_TOOLS_API } from "../../apolloClient"
import {
  CLOUDINARY_RATE_LIMITED,
  uploadMemberCardAsset,
  uploadProfilePhotoFallback,
  uploadProfilePhotoOnly,
} from "../../utils/cloudinary/profilePhotoUpload"

// The profile-photo flow (validateProfilePhoto + updatePerson + currentPerson)
// only exists on the coach-tools GraphQL endpoint - the legacy v3 endpoint
// at REACT_APP_GRAPHQL_ENDPOINT does not expose any of it. Every operation
// in this file is routed through the splitLink via this context flag.
const coachToolsContext = { context: { api: COACH_TOOLS_API } }

export const CURRENT_PERSON_PGA_ID = gql`
  query CurrentPersonPgaId {
    currentPerson {
      id
      pgaId
    }
  }
`

export const VALIDATE_PROFILE_PHOTO = gql`
  mutation ValidateProfilePhoto($input: ValidateProfilePhotoInput!) {
    validateProfilePhoto(input: $input) {
      approved
      reasons
    }
  }
`

export const UPDATE_PERSON_PROFILE_PHOTO = gql`
  mutation UpdateMemberProfilePhoto($input: CRMUpdatePersonInput!) {
    updatePerson(input: $input) {
      person {
        id
        profilePhoto
      }
      message
      success
    }
  }
`

// The v3 `Member.photo` field is read-only from the client - the v3
// schema has NO mutation input that writes it. v3 reflects the
// coach-tools `CRM::Person.profile_photo` value via a backend sync job
// (hourly / CCMS round-trip), so refreshing the page within that window
// returns the pre-sync URL. We bridge the gap with a localStorage entry
// that lets the app render the new photo immediately on subsequent
// loads until the v3 sync lands.

const STORAGE_KEY_PREFIX = "pga.profilePhoto."
// Fallback key written on every successful upload so a refresh can
// surface the new photo even if the consumer hasn't loaded its member
// id yet, or if it reads under a different id than we wrote with
// (e.g. me.id vs member(id:).id resolving via different code paths).
// Safe for this single-user-per-session app; gets overwritten by every
// new upload regardless of which member.
const STORAGE_KEY_LAST = "pga.profilePhoto.__last"

const safeStorage = () => {
  try {
    return typeof window !== "undefined" && window.localStorage
      ? window.localStorage
      : null
  } catch (e) {
    return null
  }
}

export const cacheLocalProfilePhoto = (memberId, url) => {
  const storage = safeStorage()
  if (!storage || !url) return
  try {
    if (memberId) storage.setItem(`${STORAGE_KEY_PREFIX}${memberId}`, url)
    storage.setItem(STORAGE_KEY_LAST, url)
  } catch (e) {
    // quota / privacy mode - non-fatal
  }
}

export const readLocalProfilePhoto = memberId => {
  const storage = safeStorage()
  if (!storage) return null
  try {
    if (memberId) {
      const keyed = storage.getItem(`${STORAGE_KEY_PREFIX}${memberId}`)
      if (keyed) return keyed
    }
    return storage.getItem(STORAGE_KEY_LAST)
  } catch (e) {
    return null
  }
}

// Queries that surface the avatar elsewhere in the app. Refetched after a
// successful update so every place that reads `photo`/`profilePhoto` swaps
// to the new URL without a page reload. These all live on the legacy v3
// endpoint and read `photo` from `Member` / `me`; the backend mirrors
// updatePerson writes to `CRM::Person#profile_photo` which the v3 resolver
// surfaces as `photo` on the next read.
export const PROFILE_PHOTO_REFETCH_QUERIES = [
  "ProfileBriefing",
  "USER_QUERY",
  "MembershipJourneyQuery",
  "GetMemberForEdit",
]

const sendRaygun = error => {
  if (typeof window !== "undefined" && typeof window.rg4js === "function") {
    window.rg4js("send", { error })
  }
}

const HANDLED_ERROR_NAME = "ProfilePhotoHandledError"

export const createHandledError = message => {
  const err = new Error(message)
  err.name = HANDLED_ERROR_NAME
  return err
}

export const isHandledError = err =>
  err instanceof Error && err.name === HANDLED_ERROR_NAME

/**
 * Web port of `useMemberProfilePhoto` from membership-mobile-app.
 *
 * Step machine:
 *   1. Upload to Cloudinary `profile-photos` preset (drives avatar).
 *   2. Call `validateProfilePhoto` (server-side AI Vision moderation gate).
 *   3. ONLY ON APPROVAL: upload to `member-card` preset with `public_id=pgaId`.
 *      Best-effort - failure does not block profile persistence; backend
 *      retries pick this up.
 *   4. Call `updatePerson` with the optimized profile delivery URL.
 *
 * Sequencing 1-then-3 (around the validation gate) is deliberate. See
 * pgahq/membership-mobile-app#1255 - eagerly writing the print-ready
 * member-card asset let rejected photos reach PBD's print pipeline.
 */
const useMemberProfilePhotoUpdate = ({
  pgaId: pgaIdProp,
  memberId,
  universalId,
  onSuccess,
} = {}) => {
  const client = useApolloClient()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [rejectionReasons, setRejectionReasons] = useState([])

  // Fetch pgaId from coach-tools when the caller hasn't supplied one. The
  // v3 schema (account-pga-org's default endpoint) does NOT expose pgaId
  // on Member, so we can't source it from the existing getMemberForEdit.
  const { data: personData } = useQuery(CURRENT_PERSON_PGA_ID, {
    ...coachToolsContext,
    skip: !!pgaIdProp,
    fetchPolicy: "cache-first",
  })
  const pgaId =
    pgaIdProp || (personData && personData.currentPerson && personData.currentPerson.pgaId)

  const [updatePersonMutation] = useMutation(UPDATE_PERSON_PROFILE_PHOTO, {
    ...coachToolsContext,
    refetchQueries: PROFILE_PHOTO_REFETCH_QUERIES,
    awaitRefetchQueries: false,
  })
  const [validateProfilePhotoMutation] = useMutation(
    VALIDATE_PROFILE_PHOTO,
    coachToolsContext,
  )

  const validate = useCallback(
    async imageUrl => {
      try {
        const { data } = await validateProfilePhotoMutation({
          variables: { input: { imageUrl } },
        })
        const payload = data && data.validateProfilePhoto
        const approved = !!(payload && payload.approved === true)
        const reasons = ((payload && payload.reasons) || []).filter(
          r => typeof r === "string" && r.length > 0,
        )
        return { approved, reasons }
      } catch (err) {
        // Cloudinary AI Content Analysis is metered per month. When the
        // monthly quota is exhausted the resolver bubbles up the raw
        // Cloudinary error: "Rate Limit Exceeded. Limit of 500 Cloudinary
        // AI Content Analysis operations reached..." Fail OPEN in that
        // case - this is a billing/ops problem, not a content problem,
        // and we don't want to block every member from updating their
        // photo until the monthly reset. Log to Raygun so we know the
        // moderation gate was bypassed.
        const message = err instanceof Error ? err.message : String(err)
        if (
          /Rate Limit Exceeded/i.test(message) ||
          /Cloudinary AI Content Analysis/i.test(message) ||
          /monthly billing usage/i.test(message)
        ) {
          sendRaygun(
            new Error(
              `ProfilePhoto[validate-rate-limited-fail-open]: ${message}`,
            ),
          )
          return { approved: true, reasons: [] }
        }
        throw err
      }
    },
    [validateProfilePhotoMutation],
  )

  const persistProfileUrl = useCallback(
    async profileUrl => {
      // coach-tools `updatePerson` writes CRM::Person#profile_photo - this
      // is the canonical record. v3 `Member.photo` reflects this via a
      // backend sync (NOT a client mutation - the v3 schema has no input
      // for photo). The localStorage bridge below covers the gap so the
      // user sees their new photo immediately on refresh.
      const { data } = await updatePersonMutation({
        variables: {
          input: { addresses: [], profilePhotoUrl: profileUrl },
        },
      })
      if (!data || !data.updatePerson || !data.updatePerson.success) {
        throw new Error(
          (data && data.updatePerson && data.updatePerson.message) ||
            "Could not save your photo. Please try again.",
        )
      }
    },
    [updatePersonMutation],
  )

  // Update every cached Member / CRMPerson entity's photo fields to the
  // new URL. This is the only way the avatar refreshes immediately - the
  // v3 `Member.photo` field is backed by a CCMS-mirrored record that
  // doesn't reflect the coach-tools `updatePerson` write right away, so
  // refetching `ProfileBriefing` / `USER_QUERY` / `MembershipJourneyQuery`
  // can return the OLD photo URL even after the mutation succeeded.
  const writeAvatarToCache = useCallback(
    profileUrl => {
      try {
        // Walk the entire normalized cache; any object that has a `photo`
        // or `profilePhoto` field gets the new URL written. This catches
        // Member, CRMPerson, MembershipPerson, and any other type the
        // schema names them under without us having to enumerate.
        const data = client.cache.extract()
        Object.keys(data).forEach(cacheId => {
          if (cacheId === "ROOT_QUERY" || cacheId === "ROOT_MUTATION") return
          const entity = data[cacheId]
          if (!entity || typeof entity !== "object") return
          const fields = {}
          if ("photo" in entity) fields.photo = () => profileUrl
          if ("profilePhoto" in entity) fields.profilePhoto = () => profileUrl
          if (Object.keys(fields).length > 0) {
            client.cache.modify({ id: cacheId, fields })
          }
        })
      } catch (e) {
        // cache write is a best-effort optimization; refetch will catch up
      }
    },
    [client],
  )

  const stepFailed = (step, err) => {
    const baseMessage = err instanceof Error ? err.message : String(err)
    const tagged = new Error(`ProfilePhoto[${step}]: ${baseMessage}`)
    if (err instanceof Error && err.stack) tagged.stack = err.stack
    sendRaygun(tagged)
    throw tagged
  }

  const upload = useCallback(
    async file => {
      if (!pgaId) {
        const message =
          "Your account is missing a PGA ID. Please try again later."
        setError(message)
        throw createHandledError(message)
      }
      setSaving(true)
      setError(null)
      setRejectionReasons([])

      try {
        let profileResult
        try {
          profileResult = await uploadProfilePhotoOnly(file)
        } catch (err) {
          // The m360-profile-photos preset has incoming AI moderation; when
          // its monthly quota is exhausted Cloudinary 420s the upload
          // itself. Fall back to the legacy non-moderated preset so the
          // user can still update their photo. validateProfilePhoto's
          // fail-open path handles the moderation no-op downstream.
          if (err && err.code === CLOUDINARY_RATE_LIMITED) {
            sendRaygun(
              new Error(
                `ProfilePhoto[upload-rate-limited-fallback]: ${err.message}`,
              ),
            )
            try {
              profileResult = await uploadProfilePhotoFallback(file)
            } catch (fallbackErr) {
              stepFailed("upload-fallback", fallbackErr)
            }
          } else {
            stepFailed("upload", err)
          }
        }

        const { profileUrl } = profileResult
        if (!profileUrl) {
          throw new Error("Cloudinary upload returned an empty profileUrl")
        }

        let validation
        try {
          validation = await validate(profileUrl)
        } catch (err) {
          stepFailed("validate", err)
        }

        if (!validation.approved) {
          // Guideline rejection - canonical reasons rendered verbatim. The
          // member-card asset has NOT been written, so the rejected photo
          // never reaches the PBD print pipeline. We tag the error so the
          // outer catch skips Raygun (this is an expected, user-correctable
          // outcome, not a fault).
          const reasons = validation.reasons.length
            ? validation.reasons
            : ["This photo doesn't meet our guidelines. Please try again."]
          setRejectionReasons(reasons)
          throw new Error(
            `ProfilePhoto[validate-rejected]: ${reasons.join("\n")}`,
          )
        }

        // Best-effort: a member-card upload failure should not prevent the
        // approved profile photo from saving.
        let memberCardWritten = false
        try {
          await uploadMemberCardAsset(file, { pgaId })
          memberCardWritten = true
        } catch (err) {
          sendRaygun(
            new Error(
              `ProfilePhoto[member-card-upload]: ${
                err instanceof Error ? err.message : String(err)
              }`,
            ),
          )
        }

        try {
          await persistProfileUrl(profileUrl)
        } catch (err) {
          stepFailed("persistProfileUrl", err)
        }

        writeAvatarToCache(profileUrl)
        // Persist across page refresh - v3 Member.photo syncs from
        // coach-tools on a backend job, so without this we'd revert
        // to the pre-sync URL on the next reload.
        cacheLocalProfilePhoto(memberId, profileUrl)
        if (onSuccess) onSuccess({ profileUrl, memberCardWritten })
        return { profileUrl, memberCardWritten }
      } catch (err) {
        const message =
          err instanceof Error && err.message
            ? err.message
            : "Could not update your photo. Please try again."
        const userMessage = message.replace(/^ProfilePhoto\[[^\]]+\]:\s*/, "")
        const isGuidelineRejection = /^ProfilePhoto\[validate-rejected\]:/.test(
          message,
        )
        setError(userMessage)
        if (!/^ProfilePhoto\[[^\]]+\]:/.test(message)) {
          sendRaygun(
            new Error(
              `ProfilePhoto[run]: ${err instanceof Error ? err.message : String(err)}`,
            ),
          )
        }
        if (isGuidelineRejection) {
          throw createHandledError(userMessage)
        }
        throw createHandledError(userMessage)
      } finally {
        setSaving(false)
      }
    },
    [pgaId, memberId, validate, persistProfileUrl, writeAvatarToCache, onSuccess],
  )

  return {
    upload,
    saving,
    error,
    rejectionReasons,
    reset: () => {
      setError(null)
      setRejectionReasons([])
    },
  }
}

export default useMemberProfilePhotoUpdate
